import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/auth/hooks";
import { supabase } from "../lib/supabase";
import { getEffectiveBusinessId, setBusinessIdForUser } from "../lib/businessId";

/* =========================
   TYPES
========================= */
export interface Subscription {
  id: string;
  status: "active" | "canceled" | "past_due" | "trialing" | "incomplete" | "not_started";
  current_period_start: string; // ISO
  current_period_end: string;   // ISO
  cancel_at_period_end: boolean;
  billing_cycle: "monthly" | "yearly";
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  code: string;
  plan_key?: string;
  description: string;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
  price: number;
}

export interface SubscriptionData {
  has_subscription: boolean;
  subscription?: Subscription;
  plan?: SubscriptionPlan;
  usage?: Record<string, number>;
}

/* =========================
   HOOK
========================= */
export function useSubscription() {
  const { user, business } = useAuth();
  const location = useLocation();

  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // הגנות/ייצוב
  const inFlightRef = useRef(false);
  const didRetryRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const graceTimerRef = useRef<number | null>(null);

  // שימור העסק ה"יציב" לסשן — לא מתחלף על כל refresh
  const stableBizRef = useRef<string | undefined>(undefined);

  // צילום "טוב" אחרון של מנוי תקף
  const goodSnapshotRef = useRef<{ businessId: string; data: SubscriptionData } | null>(null);

  // מפתח לוקאל ממוספר לפי משתמש (לצד תאימות לאחור למפתח הישן)
  const nsKey = user?.id ? `lf.pinnedBusinessId.${user.id}` : null;

  // טוען מצב מנוי מהקאש
  const loadCachedSubscription = useCallback(() => {
    const bizId = stableBizRef.current;
    if (!bizId) return null;
    const cacheKey = `lf.subscription.${bizId}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // בדוק שהקאש לא ישן מדי (יותר מ-5 דקות)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 300000) {
          return parsed.data as SubscriptionData;
        }
      }
    } catch (err) {
      console.error('[useSubscription] Failed to load cached subscription:', err);
    }
    return null;
  }, []);

  // שומר מצב מנוי בקאש
  const saveCachedSubscription = useCallback((subData: SubscriptionData) => {
    const bizId = stableBizRef.current;
    if (!bizId) return;
    const cacheKey = `lf.subscription.${bizId}`;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data: subData,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.error('[useSubscription] Failed to save cached subscription:', err);
    }
  }, []);

  // טען מצב מנוי מקאש בטעינה ראשונית
  useEffect(() => {
    const cached = loadCachedSubscription();
    if (cached && isFirstLoad) {
      console.log('[useSubscription] Loaded cached subscription:', cached);
      setData(cached);
      if (cached.has_subscription && stableBizRef.current) {
        goodSnapshotRef.current = { businessId: stableBizRef.current, data: cached };
      }
    }
  }, [loadCachedSubscription, isFirstLoad]);

  // נסה לקבוע עסק יציב פעם אחת מה-LS (תאימות לאחור)
  useEffect(() => {
    if (!stableBizRef.current) {
      try {
        const fromNs = nsKey ? localStorage.getItem(nsKey) || undefined : undefined;
        const fromLegacy = localStorage.getItem("businessId") || undefined;
        const chosen = fromNs || fromLegacy;
        if (chosen) stableBizRef.current = chosen;
      } catch {}
    }
  }, [nsKey]);

  // אם יש business.id וטרם נקבע — קבע אותו כיציב וסנכרן ללוקאל (חדש+ישן)
  useEffect(() => {
    if (business?.id && !stableBizRef.current) {
      stableBizRef.current = business.id;
      try {
        if (user?.id) setBusinessIdForUser(user.id, business.id);
        localStorage.setItem("businessId", business.id); // תאימות
      } catch {}
    }
  }, [business?.id, user?.id]);

  // אפשרות להחליף עסק ע"י אירוע גלובלי
  useEffect(() => {
    const onSwitch = (e: Event) => {
      const detail = (e as CustomEvent<{ businessId: string }>).detail;
      if (detail?.businessId) {
        stableBizRef.current = detail.businessId;
        try {
          if (user?.id) setBusinessIdForUser(user.id, detail.businessId);
          localStorage.setItem("businessId", detail.businessId); // תאימות
        } catch {}
        didRetryRef.current = false;
        fetchSubscription({ silent: true });
      }
    };
    window.addEventListener("lf:switch-business", onSwitch as EventListener);
    return () => window.removeEventListener("lf:switch-business", onSwitch as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // האזנה לשינוי ב-localStorage
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      const isNs = nsKey && e.key === nsKey;
      const isLegacy = e.key === "businessId";
      if ((isNs || isLegacy) && e.newValue) {
        stableBizRef.current = e.newValue;
        didRetryRef.current = false;
        fetchSubscription({ silent: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nsKey]);

  // ✅ Bootstrap מה־DB כשאין לנו business יציב
  useEffect(() => {
    if (!user?.id) return;
    if (stableBizRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const bid = await getEffectiveBusinessId(user.id);
        if (cancelled || !bid) return;
        stableBizRef.current = bid;
        try {
          setBusinessIdForUser(user.id, bid);
          localStorage.setItem("businessId", bid); // תאימות
        } catch {}
        didRetryRef.current = false;
        fetchSubscription({ silent: true });
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const effectiveBusinessId = stableBizRef.current;

  // עמודי חוץ/הרשמה - לא טוען מנוי
  const isExternalPage = location.pathname.startsWith("/book/");
  const isRegistrationPage = location.pathname.includes("/register");

  // ——————————————————————————————
  //  Core fetcher
  // ——————————————————————————————
  const fetchSubscription = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        if (isExternalPage || isRegistrationPage) {
          setError(null);
          setLoading(false);
          return;
        }

        if (!user?.id || !effectiveBusinessId) {
          console.log("[useSubscription] Missing user/business (transient), keeping last data.", {
            user: !!user?.id,
            effectiveBusinessId,
          });
          setError(null);
          setLoading(false);
          return;
        }

        if (!opts?.silent) setLoading(true);
        console.log("[useSubscription] RPC get_business_subscription →", {
          effBiz: effectiveBusinessId,
          path: location.pathname,
        });

        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "get_business_subscription",
          { p_business_id: effectiveBusinessId }
        );

        if (rpcError) {
          console.error("[useSubscription] RPC error:", rpcError);
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        const normalized: SubscriptionData = rpcData ?? { has_subscription: false };
        console.log("[useSubscription] RPC result:", normalized);

        // אם מגיעה תשובה "אין מנוי" אבל יש לנו צילום טוב לעסק אחר — לא נדרוס UI
        if (
          !normalized?.has_subscription &&
          goodSnapshotRef.current &&
          goodSnapshotRef.current.businessId !== effectiveBusinessId
        ) {
          console.log("[useSubscription] Ignoring negative result from other business; keeping good snapshot.");
          setData(goodSnapshotRef.current.data);
          setError(null);
          setLoading(false);
          return;
        }

        // אם יש מנוי תקף — לשמור צילום טוב ובקאש
        if (normalized?.has_subscription) {
          goodSnapshotRef.current = { businessId: effectiveBusinessId, data: normalized };
          saveCachedSubscription(normalized);
        }

        // ריטריי עדין (וובהוק עדיין מסנכרן)
        if (
          !didRetryRef.current &&
          (!normalized?.has_subscription || normalized?.subscription?.status === "not_started")
        ) {
          didRetryRef.current = true;
          if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
          retryTimerRef.current = window.setTimeout(() => {
            console.log("[useSubscription] retry (webhook lag?)…");
            fetchSubscription({ silent: true });
          }, 1200) as unknown as number;
        }

        setData(normalized);
        setError(null);

        // טעינה ראשונה: תן grace period של 3 שניות לפני שמאפשרים בדיקת מנוי
        if (isFirstLoad) {
          if (graceTimerRef.current) window.clearTimeout(graceTimerRef.current);
          graceTimerRef.current = window.setTimeout(() => {
            console.log('[useSubscription] Grace period ended, allowing subscription checks');
            setLoading(false);
            setIsFirstLoad(false);
          }, 3000) as unknown as number;
        } else {
          setLoading(false);
        }
      } catch (err: any) {
        console.error("[useSubscription] Unexpected error:", err);
        setError(err?.message ?? "Unknown error");
        setLoading(false);
      } finally {
        inFlightRef.current = false;
      }
    },
    [user?.id, effectiveBusinessId, isExternalPage, isRegistrationPage, location.pathname]
  );

  // טעינה ראשונית + כשה-user/biz מתייצבים לראשונה
  useEffect(() => {
    didRetryRef.current = false;
    fetchSubscription();
  }, [fetchSubscription]);

  // רענון כשמסך חוזר לפוקוס
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") {
        fetchSubscription({ silent: true });
      }
    };
    window.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchSubscription]);

  // ניקוי טיימרים
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
      if (graceTimerRef.current) window.clearTimeout(graceTimerRef.current);
    };
  }, []);

  // Trial is always available (no time limitation)
  const trialAvailable = true;

  /* =========================
     Feature gates & helpers
  ========================= */
  const isFeatureAvailable = (featureCode: string): boolean => {
    if (featureCode === "loyalty_program") return true;
    if (featureCode === "ai_chat" && trialAvailable) return true;
    if (!data?.has_subscription) return false;
    return data.plan?.features?.[featureCode] === true;
  };

  const getFeatureLimit = (featureCode: string): number | null => {
    if (!data?.has_subscription) return 0;
    return data.plan?.limits?.[featureCode] ?? null;
  };

  const getFeatureUsage = (featureCode: string): number => {
    if (!data?.has_subscription) return 0;
    return data.usage?.[featureCode] ?? 0;
  };

  const getFeatureRemaining = (featureCode: string): number | null => {
    const limit = getFeatureLimit(featureCode);
    const usage = getFeatureUsage(featureCode);
    if (limit === null) return null;
    return Math.max(0, limit - usage);
  };

  const hasEnoughForAction = useCallback(
    async (featureCode: string, amount: number): Promise<boolean> => {
      try {
        if (!data?.has_subscription) return false;
        if (!isFeatureAvailable(featureCode)) return false;

        const limit = getFeatureLimit(featureCode);
        if (limit === null) return true;

        const bizId = stableBizRef.current;
        if (featureCode === "ai_tokens" && bizId) {
          const { data: tokenData, error } = await supabase.rpc("get_remaining_tokens", {
            p_business_id: bizId,
          });
          if (error) return false;
          return (tokenData?.tokens_remaining ?? 0) >= amount;
        }

        const remaining = getFeatureRemaining(featureCode);
        return remaining === null || remaining >= amount;
      } catch {
        return false;
      }
    },
    [data?.has_subscription]
  );

  /* =========================
     EXPORT (API)
  ========================= */
  return {
    subscription: data?.has_subscription ? { ...data.subscription!, plan: data.plan! } : null,
    hasSubscription: data?.has_subscription ?? false,
    loading,
    error,
    trialAvailable,
    isFeatureAvailable,
    getFeatureLimit,
    getFeatureUsage,
    getFeatureRemaining,
    hasEnoughForAction,
    refresh: () => fetchSubscription({ silent: true }),
    __raw: data,
  };
}
